import React, { useEffect } from 'react';
import SEO from '../../components/SEO';
import LandingNavBar from '../../components/LandingNavBar';
import LandingFooter from '../../components/LandingFooter';
import { SEOHero, SEOSection, FeatureGrid, BenefitList, InternalLinkGrid, CTABanner } from '../../components/SEOPageComponents';
import { Shield, FileText, Zap, Scale, Receipt, AlertTriangle, CheckCircle2 } from 'lucide-react';

export default function LhdnEInvoice() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <>
      <SEO
        title="LHDN e-Invoice — Compliance Guide for Malaysian Hotels & Businesses"
        description="Complete guide to LHDN e-invoice compliance. Understand MyInvois requirements, submission deadlines, and how StayMii automates LHDN e-invoicing for hotels, PMS, and channel managers."
        keywords="lhdn e invoice, lhdn einvoice, lhdn e-invoice compliance, lhdn myinvois, lhdn e invoice hotel, lhdn e invoice requirements, lhdn digital invoice"
        canonicalUrl="/lhdn-e-invoice"
      />
      <div className="min-h-screen bg-white">
        <LandingNavBar />
        <SEOHero
          badge="LHDN Compliance"
          h1="LHDN e-Invoice: Everything Hotels & Businesses Need to Know"
          subtitle="LHDN requires all Malaysian businesses to submit e-invoices digitally via MyInvois. StayMii automates LHDN e-invoice compliance for hotels, PMS systems, channel managers, and short-term rental operators."
        />

        <SEOSection h2="Understanding LHDN e-Invoice Requirements" description="LHDN (Lembaga Hasil Dalam Negeri Malaysia) has mandated electronic invoicing for all businesses. Here's what you need to know about compliance.">
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: FileText, title: 'What is Required?', desc: 'All business-to-business (B2B) and business-to-consumer (B2C) invoices must be submitted electronically to LHDN via the MyInvois system. This includes hotel invoices, OTA booking invoices, and service charges.' },
              { icon: AlertTriangle, title: 'Penalties for Non-Compliance', desc: 'Businesses that fail to comply with LHDN e-invoice requirements face fines of up to RM200,000 or imprisonment of up to 3 years. Regular audits are conducted.' },
              { icon: Shield, title: 'How StayMii Ensures Compliance', desc: 'StayMii integrates with your PMS or channel manager to auto-generate and submit LHDN-compliant e-invoices. Every invoice is validated before submission to prevent rejections.' },
            ].map((item, i) => (
              <div key={i} className="bg-slate-50 rounded-2xl p-7 border border-slate-200">
                <item.icon className="w-8 h-8 text-[#2563EB] mb-4" />
                <h3 className="text-lg font-bold text-slate-900 mb-3">{item.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </SEOSection>

        <SEOSection h2="LHDN e-Invoice for Hotels & Hospitality" bg="bg-slate-50" description="Hotels face unique e-invoicing challenges: OTA bookings, walk-in guests, multi-rate rooms, and service charges. StayMii handles it all.">
          <BenefitList items={[
            { title: 'OTA Booking Invoices', desc: 'Automatically generate e-invoices for bookings from Booking.com, Agoda, Expedia via your channel manager.' },
            { title: 'Walk-In & Direct Booking Invoices', desc: 'PMS-integrated invoicing for direct reservations and walk-in guests with automatic tax calculation.' },
            { title: 'Service Charge & Tax Handling', desc: 'Automatic SST, tourism tax, and service charge calculations compliant with LHDN requirements.' },
            { title: 'Multi-Property Support', desc: 'Manage e-invoice compliance across multiple properties from a single dashboard.' },
            { title: 'Credit & Debit Note Support', desc: 'Handle refunds, adjustments, and corrections with LHDN-compliant credit and debit notes.' },
          ]} />
        </SEOSection>

        <SEOSection h2="Related Resources">
          <InternalLinkGrid links={[
            { to: '/e-invoice-malaysia', title: 'e-Invoice Malaysia Guide', desc: 'Complete overview of Malaysia e-invoicing' },
            { to: '/myinvois-api', title: 'MyInvois API Integration', desc: 'Technical details on MyInvois API connection' },
            { to: '/hotel-e-invoice-system', title: 'Hotel e-Invoice System', desc: 'Purpose-built for hotel operations' },
            { to: '/pms-e-invoice-integration', title: 'PMS Integration', desc: 'Connect your property management system' },
            { to: '/channel-manager-e-invoice', title: 'Channel Manager e-Invoice', desc: 'Automate OTA booking invoices' },
            { to: '/airbnb-e-invoice-malaysia', title: 'Airbnb e-Invoice', desc: 'Compliance for short-term rental hosts' },
          ]} />
        </SEOSection>

        <CTABanner title="Automate Your LHDN e-Invoice Compliance" subtitle="StayMii ensures every invoice is compliant before it reaches LHDN." />
        <LandingFooter />
      </div>
    </>
  );
}
